import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { isDraft } from "../../../shared/connectors/isDraft";
import { withoutListCatalogs } from "../../../shared/connectors/listRows";
import { REQUEST_CONNECTOR_SHARE_FLAG } from "../../../shared/featureFlagNames";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import type { GrantEntry } from "../../../shared/rbac/roles";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useQuery } from "../../hooks/useQuery";
import {
  connectorVariants,
  resolveConnectorConfig,
} from "../../lib/connectorDirectory";
import { connectorRawTypeLabel } from "../../lib/connector";
import { makeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { AccessMapPanel } from "../AccessMapPanel";
import { ConnectorStatusPill } from "../ConnectorStatusPill";
import { Button } from "../Button";
import { ConfirmModal } from "../ConfirmModal";
import { ConnectorLogo } from "../ConnectorLogo";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { DeleteConnectorModalBody } from "../Connectors/DeleteConnectorModal";
import { DangerSection } from "../Settings/DangerSection";
import { HeaderTitleStack } from "../HeaderTitleStack";
import { Icon } from "../Icon";
import { Modal } from "../Modal";
import {
  useScopeAccessCloseWarning,
  WarningContent,
  WarningFooter,
} from "../ScopeAccessCloseWarning";
import { TabLinkButton } from "../TabButton";
import { Text } from "../Text";
import { Toggle } from "../Toggle";
import { WavyLine } from "../WavyLine";
import { AccessTab } from "./AccessTab";
import { Form } from "./Form";
import { SecureBadge } from "./SecureBadge";
import { UsageTab } from "./UsageTab";
import { useConnectorAuthorize } from "./useConnectorAuthorize";
import { VariantPicker } from "./VariantPicker";

type Tab = "usage" | "settings" | "access";

const Header = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
`;

const HeaderLeft = styled("div")`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Body = styled("div")`
  padding: 16px;
  flex: 1;
  border: 0.5px solid var(--border-alpha);
  border-left: 0;
  border-right: 0;
  overflow: auto;
`;

const Footer = styled("div")`
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FooterRight = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Tabs = styled("div")`
  display: flex;
  flex-shrink: 0;
  gap: 8px;
`;

const HeaderRight = styled("div")`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
`;

const Guide = styled("div")`
  opacity: 0.3;
  display: flex;
  align-items: center;
  gap: 4px;
`;

type Props = {
  id: string;
  onClose: () => void;
};

export function ConnectorModal({ id, onClose }: Props) {
  const z = useZero();
  const [appRow, appResult] = useQuery(
    queries.loadConnectorDetails({ connectedAppId: id })
  );
  // Server hydration can momentarily drop the row while grants and relations
  // land across syncs; hold the last seen row so the modal never blanks.
  const heldApp = useRef<{ id: string; app: NonNullable<typeof appRow> }>(null);
  if (appRow) heldApp.current = { id, app: appRow };
  const app =
    appRow ?? (heldApp.current?.id === id ? heldApp.current.app : undefined);

  const appConfig = resolveConnectorConfig(app?.identifier);
  const hasSettings = appConfig != null;

  const draft = !app || isDraft(app);

  const { tab } = useCurrentUrl(routes.modal.connector, { id });
  const rawTab = tab as Tab;
  const activeTab: Tab =
    !hasSettings && rawTab === "settings" ? "usage" : rawTab;
  const setActiveTab = (next: Tab) =>
    navigate(routes.modal.connector, { id, tab: next });
  const visitedAccess = useRef(activeTab === "access");
  if (activeTab === "access") visitedAccess.current = true;
  const canWrite = useHasAccess("connected_app:write", app?.id);
  const canShare = useHasAccess("connected_app:share", app?.id);

  // The fetched model catalog is scratch for picking, persisted only so the
  // async fetch can sync it to the client; drop it when the modal closes so it
  // doesn't linger in stored config. Skipped on the paths that remove the
  // connector, since there'd be no row left to write. This can't guarantee a
  // clean picker on reopen — a refresh in flight at close lands afterward and
  // re-adds catalog rows — so the reopen guarantee lives in the open clear below.
  const catalogCleanup = useRef<{
    id: string;
    name: string;
    identifier: string;
    urlPattern: string;
    configuration: Record<string, string>;
    canWrite: boolean;
  } | null>(null);
  const skipCatalogCleanup = useRef(false);
  if (app) {
    catalogCleanup.current = {
      id: app.id,
      name: app.name,
      identifier: app.identifier,
      urlPattern: app.urlPattern,
      configuration: app.configuration,
      canWrite,
    };
  }
  useEffect(
    () => () => {
      const latest = catalogCleanup.current;
      if (
        skipCatalogCleanup.current ||
        !latest?.canWrite ||
        !latest.name.trim()
      )
        return;
      const configuration = withoutListCatalogs(latest.configuration);
      if (
        Object.keys(configuration).length ===
        Object.keys(latest.configuration).length
      )
        return;
      z.mutate(
        sharedMutators.updateConnectedApp({
          connectedAppId: latest.id,
          name: latest.name,
          identifier: latest.identifier,
          urlPattern: latest.urlPattern,
          configuration,
        })
      );
    },
    [z]
  );
  // Strip any leftover catalog once when the connector opens, before this
  // session fetches its own — the race-free point to guarantee a clean picker,
  // regardless of a refresh that landed after a previous close.
  const catalogClearedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!app || !canWrite || !app.name.trim()) return;
    if (catalogClearedFor.current === app.id) return;
    catalogClearedFor.current = app.id;
    const configuration = withoutListCatalogs(app.configuration);
    if (
      Object.keys(configuration).length ===
      Object.keys(app.configuration).length
    )
      return;
    z.mutate(
      sharedMutators.updateConnectedApp({
        connectedAppId: app.id,
        name: app.name,
        identifier: app.identifier,
        urlPattern: app.urlPattern,
        configuration,
      })
    );
  }, [app, canWrite, z]);
  const [guideOn, setGuideOn] = useState(draft);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [authorizePrompt, setAuthorizePrompt] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [currentUser] = useQuery(queries.currentUser());

  // Secret values never replicate back to the client, so which masked fields
  // were filled this session is tracked here — the modal outlives the tab
  // switches that unmount the settings form. Switching variant clears the
  // row's secrets, so forget them then too.
  const [locallySet, setLocallySet] = useState<ReadonlySet<string>>(new Set());
  const onFieldSet = useCallback((key: string) => {
    setLocallySet((prev) => (prev.has(key) ? prev : new Set([...prev, key])));
  }, []);
  const identifier = app?.identifier;
  useEffect(() => {
    setLocallySet(new Set());
  }, [id, identifier]);

  const auth = useConnectorAuthorize({
    app,
    definition: appConfig?.definition,
    locallySet,
    onEdit: () => setIsDirty(true),
  });

  const flagOn = useFeatureFlag(REQUEST_CONNECTOR_SHARE_FLAG) === true;
  const [pendingRequest] = useQuery(
    queries.grantRequestForScope({ scopeId: id })
  );
  const canRequestShare = flagOn && canWrite && !canShare;
  const isRequester =
    pendingRequest != null &&
    pendingRequest.requestedByUserId === currentUser?.id;
  const showPendingReview = flagOn && pendingRequest != null;
  const showCompose = canRequestShare && pendingRequest == null;
  const requestMode = showPendingReview || showCompose;
  const [requestGrants, setRequestGrants] = useState<GrantEntry[] | null>(null);

  // Seed the local grant set once: from the pending request's proposal if one
  // exists, otherwise from the connector's current grants (the requester's own),
  // so the submitted request carries the full desired set and approving it via
  // applyScopeRoleGrants doesn't strip the creator's access.
  useEffect(() => {
    if (!requestMode || requestGrants !== null) return;
    const source: readonly {
      userGroupId?: string | null;
      userId?: string | null;
      serviceAccountId?: string | null;
      builtinRoleId?: string | null;
      customRoleId?: string | null;
    }[] = pendingRequest
      ? pendingRequest.requestedGrants
      : (app?.roleGrantsAsScope ?? []);
    setRequestGrants(
      source
        .map((g) => ({
          userGroupId: g.userGroupId ?? undefined,
          userId: g.userId ?? undefined,
          serviceAccountId: g.serviceAccountId ?? undefined,
          builtinRoleId: g.builtinRoleId ?? undefined,
          customRoleId: g.customRoleId ?? undefined,
        }))
        .filter((g) => g.userGroupId || g.userId || g.serviceAccountId)
    );
  }, [requestMode, requestGrants, pendingRequest, app]);

  const { stagedGrantsRef, requestClose, closeWarningModal } =
    useScopeAccessCloseWarning({
      singular: "member or group",
      plural: "members and groups",
    });

  const bodyRef = useRef<HTMLDivElement | null>(null);
  const autoFocusedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!draft || autoFocusedFor.current === id) return;
    const body = bodyRef.current;
    if (!body) return;
    requestAnimationFrame(() => {
      const input = body.querySelector<HTMLInputElement>(
        'input[type="text"]:not([readonly])'
      );
      if (input) {
        input.focus({ preventScroll: true });
        body.scrollTop = 0;
        autoFocusedFor.current = id;
      }
    });
  }, [draft, id]);

  if (!app) {
    if (appResult?.type !== "complete") {
      return (
        <Modal
          open
          onClose={onClose}
          style={{
            width: 680,
            height: 760,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <WavyLine />
        </Modal>
      );
    }
    return null;
  }

  if (!hasSettings && draft) {
    return null;
  }

  const unsaved = canWrite && app.fresh;

  const clientCredentials =
    appConfig?.definition.oauth?.grantType(app.configuration) ===
    "client_credentials";

  const trimmedName = app.name?.trim();
  const typeLabel = connectorRawTypeLabel(app);
  const showType = Boolean(trimmedName) && trimmedName !== typeLabel;

  const discardDraft = () => {
    skipCatalogCleanup.current = true;
    z.mutate(
      sharedMutators.tidyUpFreshConnectedApp({
        connectedAppId: app.id,
      })
    );
    onClose();
  };

  const dismissModal = () => {
    if (!unsaved) {
      onClose();
      return;
    }
    if (isDirty) {
      setConfirmDiscard(true);
      return;
    }
    discardDraft();
  };

  // reportValidity can't flash its hint on a field in a hidden tab.
  const formIsValid = () => {
    if (hasSettings && activeTab !== "settings") {
      flushSync(() => setActiveTab("settings"));
    }
    return bodyRef.current?.querySelector("form")?.reportValidity() ?? true;
  };

  const publishAndClose = () => {
    z.mutate(sharedMutators.publishConnectedApp({ connectedAppId: app.id }));
    onClose();
  };

  const saveAndClose = () => {
    const name = app.name?.trim() ? app.name : fallbackName();
    const urlPattern = app.urlPattern || fallbackUrlPattern();

    if (name !== app.name || urlPattern !== app.urlPattern) {
      z.mutate(
        sharedMutators.updateConnectedApp({
          connectedAppId: app.id,
          name,
          identifier: app.identifier,
          urlPattern,
          configuration: app.configuration,
        })
      );
    }
    if (auth.canAuthorize) {
      // Client-credentials connections need no consent step, so connect in
      // the background; only flows that open a popup get the prompt.
      if (clientCredentials) {
        auth.authorize();
      } else {
        setAuthorizePrompt(true);
        return;
      }
    }
    publishAndClose();
  };

  const authorizeAndClose = async () => {
    setAuthorizePrompt(false);
    if ((await auth.authorize()) === "error") return;
    publishAndClose();
  };

  const fallbackName = () => {
    const firstName = currentUser?.name?.split(" ")[0] || currentUser?.email;
    return firstName && app.identifier === "apiKey" && appConfig
      ? `${firstName}'s ${appConfig.name}`
      : (appConfig?.name ?? app.name);
  };

  const fallbackUrlPattern = () =>
    appConfig?.definition.defaultUrlPattern(app.configuration ?? {}) ?? "";

  // A fresh draft discards on close but must validate on save; a saved
  // connector is only worth checking once the user has actually edited it.
  const close = () => {
    if (canWrite && isDirty && !app.fresh && !formIsValid()) return;
    requestClose(dismissModal);
  };
  const finish = () => {
    if (canWrite && (isDirty || app.fresh) && !formIsValid()) return;
    requestClose(canWrite && app.fresh ? saveAndClose : dismissModal);
  };

  const goToAccess = () => {
    if (formIsValid()) setActiveTab("access");
  };

  const accessCanManage = showCompose || (!requestMode && canShare);

  // Flag the principals the request would ADD (in the proposed set but not in the
  // connector's current grants) with a "New" badge — so both the requester
  // composing and the reviewer approving see the delta, not one flat list.
  const newPrincipalIds = (() => {
    if (!requestMode) return undefined;
    const current = new Set(
      (app.roleGrantsAsScope ?? []).flatMap((g) =>
        [g.userId, g.userGroupId, g.serviceAccountId].filter(
          (x): x is string => x != null
        )
      )
    );
    return new Set(
      (requestGrants ?? [])
        .map((g) => g.userId ?? g.userGroupId ?? g.serviceAccountId)
        .filter((id): id is string => id != null && !current.has(id))
    );
  })();

  const proposesShare = (requestGrants ?? []).some(
    (g) =>
      g.userGroupId ||
      g.serviceAccountId ||
      (g.userId != null && g.userId !== currentUser?.id)
  );

  const submitRequest = () => {
    if (app.fresh && !formIsValid()) return;
    const name = app.name?.trim() ? app.name : fallbackName();
    const urlPattern = app.urlPattern || fallbackUrlPattern();
    if (app.fresh && (name !== app.name || urlPattern !== app.urlPattern)) {
      z.mutate(
        sharedMutators.updateConnectedApp({
          connectedAppId: app.id,
          name,
          identifier: app.identifier,
          urlPattern,
          configuration: app.configuration,
        })
      );
    }
    if (app.fresh) {
      z.mutate(sharedMutators.publishConnectedApp({ connectedAppId: app.id }));
    }
    z.mutate(
      sharedMutators.requestScopeRoleGrants({
        requestId: nanoid(12),
        scopeId: app.id,
        grants: (requestGrants ?? []).map((g) => ({
          userGroupId: g.userGroupId,
          userId: g.userId,
          serviceAccountId: g.serviceAccountId,
          builtinRoleId: g.builtinRoleId,
          customRoleId: g.customRoleId,
        })),
      })
    );
    onClose();
  };

  const cancelRequest = () => {
    if (pendingRequest) {
      z.mutate(
        sharedMutators.cancelGrantRequest({ requestId: pendingRequest.id })
      );
    }
    onClose();
  };

  const approveRequest = () => {
    if (pendingRequest) {
      z.mutate(
        sharedMutators.approveGrantRequest({ requestId: pendingRequest.id })
      );
    }
    onClose();
  };

  const dismissRequest = () => {
    if (pendingRequest) {
      z.mutate(
        sharedMutators.dismissGrantRequest({ requestId: pendingRequest.id })
      );
    }
    onClose();
  };

  const requesterName =
    pendingRequest?.requestedByUser?.name ||
    pendingRequest?.requestedByUser?.email ||
    "someone";

  return (
    <>
      <Modal
        open
        onClose={close}
        style={{
          width: 680,
          height: 760,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Header>
          <HeaderLeft>
            <ConnectorLogoContainer
              size={32}
              borderRadius={8}
              hoverBackground={false}
            >
              <ConnectorLogo app={app} size={16} />
            </ConnectorLogoContainer>
            <HeaderTitleStack>
              <Text
                variant="text-regular-md"
                color="var(--text-primary)"
                ellipsis
              >
                {trimmedName || typeLabel}
              </Text>
              {showType && (
                <Text
                  variant="text-small"
                  color="var(--text-secondary)"
                  ellipsis
                >
                  {typeLabel}
                </Text>
              )}
            </HeaderTitleStack>
          </HeaderLeft>
          <Tabs>
            {hasSettings && (
              <TabLinkButton
                to={makeUrl(routes.modal.connector, {
                  id,
                  tab: "settings",
                })}
                className={activeTab === "settings" ? "active" : undefined}
              >
                <Icon name="cog" size={16} />
                <Text variant="text-small-md">Settings</Text>
              </TabLinkButton>
            )}
            <TabLinkButton
              to={makeUrl(routes.modal.connector, { id, tab: "access" })}
              className={activeTab === "access" ? "active" : undefined}
            >
              <Icon name="lock" size={16} />
              <Text variant="text-small-md">Access</Text>
            </TabLinkButton>
            {!draft && (
              <TabLinkButton
                to={makeUrl(routes.modal.connector, { id, tab: "usage" })}
                className={activeTab === "usage" ? "active" : undefined}
              >
                <Icon name="chart-no-axes-column" size={16} />
                <Text variant="text-small-md">Usage</Text>
              </TabLinkButton>
            )}
          </Tabs>
          <HeaderRight>
            {activeTab === "settings" && (
              <>
                <Guide>
                  <Text variant="text-small-md">Guide</Text>
                </Guide>
                <Toggle
                  isOn={guideOn}
                  onToggle={() => setGuideOn((was) => !was)}
                  disabled={!canWrite}
                />
                <Icon name="slash" size={12} color="var(--border-alpha)" />
              </>
            )}
            <Button size="l" variant="secondary" icon="x" onClick={close} />
          </HeaderRight>
        </Header>
        <Body ref={bodyRef} onChange={() => setIsDirty(true)}>
          {activeTab === "usage" ? (
            <UsageTab
              connectedAppId={id}
              loading={appResult?.type !== "complete"}
              usageMeasures={appConfig?.definition.usage?.measures ?? null}
            />
          ) : activeTab === "access" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {!app.fresh && (
                <AccessMapPanel
                  nodeId={app.id}
                  noun="connector"
                  guard={(go) => {
                    if (canWrite && isDirty && !formIsValid()) return;
                    requestClose(go);
                  }}
                />
              )}
              <AccessTab
                app={app as unknown as Parameters<typeof AccessTab>[0]["app"]}
                canManage={accessCanManage}
                stagedGrantsRef={stagedGrantsRef}
                onEdit={() => setIsDirty(true)}
                localGrants={requestMode ? (requestGrants ?? []) : undefined}
                onChangeLocalGrants={requestMode ? setRequestGrants : undefined}
                newPrincipalIds={newPrincipalIds}
              />
            </div>
          ) : hasSettings && appConfig ? (
            <>
              {canWrite &&
                app.fresh &&
                connectorVariants(appConfig).length > 1 && (
                  <VariantPicker appId={app.id} appConfig={appConfig} />
                )}
              {/* Keyed by variant so switching remounts the form's uncontrolled
                  inputs — a secret typed for the old variant would otherwise
                  linger on screen after the row's secrets are cleared. */}
              <Form
                key={appConfig.identifier}
                definition={appConfig.definition}
                showInstructions={guideOn}
                app={app}
                disabled={!canWrite}
                onEdit={() => setIsDirty(true)}
                locallySet={locallySet}
                onFieldSet={onFieldSet}
                auth={auth}
              />
              {!draft && canWrite && (
                <DangerSection
                  style={{ marginTop: 16 }}
                  title="Delete connector"
                  description="Permanently deletes this connector and its configuration. Any steps that use it will need a replacement."
                  actionLabel="Delete connector"
                  renderConfirm={({ open, onClose: closeConfirm }) =>
                    open ? (
                      <Modal open onClose={closeConfirm} alert>
                        <DeleteConnectorModalBody
                          appId={app.id}
                          onClose={closeConfirm}
                          onDeleted={() => {
                            skipCatalogCleanup.current = true;
                            onClose();
                          }}
                        />
                      </Modal>
                    ) : null
                  }
                />
              )}
            </>
          ) : null}
        </Body>
        <Footer>
          <SecureBadge />
          <FooterRight>
            {!showPendingReview && <ConnectorStatusPill app={app} />}
            {requestMode ? (
              showPendingReview ? (
                isRequester ? (
                  <>
                    <Text variant="text-small" color="var(--text-secondary)">
                      Pending approval
                    </Text>
                    <Button variant="subtle" size="l" onClick={cancelRequest}>
                      Cancel request
                    </Button>
                  </>
                ) : (
                  <>
                    <Text
                      variant="text-small"
                      color="var(--text-secondary)"
                      ellipsis
                    >
                      Requested by {requesterName}
                    </Text>
                    <Button variant="subtle" size="l" onClick={dismissRequest}>
                      Dismiss
                    </Button>
                    <Button variant="primary" size="l" onClick={approveRequest}>
                      Approve
                    </Button>
                  </>
                )
              ) : proposesShare ? (
                <Button variant="primary" size="l" onClick={submitRequest}>
                  Request approval
                </Button>
              ) : (
                <Button
                  variant="primary"
                  size="l"
                  onClick={finish}
                  disabled={auth.isPending}
                >
                  {canWrite && app.fresh ? "Save" : "Done"}
                </Button>
              )
            ) : canWrite && app.fresh && !visitedAccess.current ? (
              <Button variant="primary" size="l" onClick={goToAccess}>
                Next
              </Button>
            ) : (
              <Button
                variant="primary"
                size="l"
                onClick={finish}
                disabled={auth.isPending}
              >
                {canWrite && app.fresh ? "Save" : "Done"}
              </Button>
            )}
          </FooterRight>
        </Footer>
      </Modal>
      {closeWarningModal}
      {authorizePrompt && appConfig && (
        <Modal open onClose={() => setAuthorizePrompt(false)} alert>
          <WarningContent>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <Text variant="text-regular-md" color="var(--text-primary)">
                Authorize now?
              </Text>
              <Text variant="text-regular" color="var(--text-secondary)">
                This connector will be saved as a draft until it’s authorized
                with {appConfig.definition.name}.
              </Text>
            </div>
            <WarningFooter>
              <Button
                size="l"
                variant="secondary"
                onClick={() => {
                  setAuthorizePrompt(false);
                  publishAndClose();
                }}
              >
                Save as draft
              </Button>
              <Button size="l" onClick={authorizeAndClose}>
                Authorize
              </Button>
            </WarningFooter>
          </WarningContent>
        </Modal>
      )}
      <ConfirmModal
        open={confirmDiscard}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={discardDraft}
        title="Discard new connection?"
        description="Your changes will be lost if you continue."
        confirmLabel="Discard"
        destructive
      />
    </>
  );
}
